import { z } from "zod";

// A 200 response alone is not enough to replace the user during uncertain logout.
export const authUserSchema = z.object({
  id: z.string().min(1),
  username: z.string().min(1),
  displayName: z.string(),
  email: z.string(),
  role: z.string(),
  teamMemberId: z.string().nullable(),
  forcePasswordChange: z.boolean().optional(),
  specialties: z.array(z.string()),
  managedSpecialties: z.array(z.string()).optional(),
  passwordExpiryDays: z.number().optional(),
  isSuperAdmin: z.boolean().optional(),
  impersonating: z.boolean().optional(),
  originalDisplayName: z.string().optional(),
});

export type AuthUser = z.infer<typeof authUserSchema>;

/** What a successful identity switch must leave signed in. */
export type SwitchExpectation = { kind: "start"; targetUserId: string } | { kind: "stop" };

/**
 * True only when the confirmed server identity is the outcome the switch asked for. Any other
 * account (for example another tab signing in mid-switch) is not this switch's success.
 */
export function switchReachedExpected(expected: SwitchExpectation, confirmed: AuthUser | null): boolean {
  if (!confirmed) return false;
  if (expected.kind === "start") return confirmed.id === expected.targetUserId && confirmed.impersonating === true;
  return !confirmed.impersonating;
}

/** The server's own wording for its 409 when the session changes during a switch. */
export const SESSION_CHANGED_MESSAGE = "Your sign-in changed. Refresh or sign in again.";

/**
 * The error a switch reports when its POST succeeded but the confirmed account is not the one it
 * asked for. Shaped like apiRequest's `"<status>: <json body>"` errors so callers' parseApiError
 * shows the message instead of a generic fallback.
 */
export function sessionChangedDuringSwitch(): Error {
  return new Error(`409: ${JSON.stringify({ message: SESSION_CHANGED_MESSAGE })}`);
}
